/**
 * Who authored a chat message. 'assistant' is the GatherCo AI support agent (Pebbl),
 * 'user' is the signed-in trade customer.
 */
export type AiChatMessageRole = 'user' | 'assistant';

/**
 * Frame types emitted on the `POST /pebbl/public/chat` SSE stream.
 *
 * `reasoning` has not been observed on this channel but the API guide asks us to tolerate it
 * (a model swap could start producing it); anything not listed here must be ignored, never
 * treated as an error.
 */
export type AiChatStreamFrameType =
  | 'tool_start'
  | 'tool_end'
  | 'answer'
  | 'reasoning'
  | 'usage'
  | 'complete'
  | 'component';

/**
 * A `component` frame's payload: a strip of product cards to show inside the reply.
 *
 * `skus` are opaque catalogue SKUs in display order, already de-duplicated by the service. They
 * contain `/`, brackets, `+` and spaces, so they are never parsed, trimmed or re-cased - only
 * percent-encoded when they go into a URL. `product_cards` is the only component type today;
 * anything else is ignored.
 */
export interface AiChatProductCardsComponent {
  type: 'product_cards';
  skus: string[];
}

/**
 * One decoded `data:` line from the stream. `data` is deliberately loose - each frame type
 * carries a different shape and unknown types are dropped without inspecting them.
 */
export interface AiChatStreamFrame {
  type: AiChatStreamFrameType | string;
  data: Record<string, unknown>;
}

/**
 * A tool the agent ran while preparing its answer. Rendered as the "Pebbl is doing X" line so a
 * multi-second gather (a lookup can chain five or more tools before the first `answer` frame)
 * never shows an empty bubble.
 */
export class AiChatToolActivityModel {
  id!: string;
  name!: string;
  /** Human-readable label from `tool_start`, e.g. "Looking up the answer". */
  label!: string;
  /** Result blurb from `tool_end`; the API may send an empty string. */
  summary?: string;
  /** Flipped by the matching `tool_end` frame. */
  is_complete = false;
}

/**
 * Token/cost accounting for one turn (`usage` frame). Only `elapsed_seconds` is shown to the
 * customer ("Worked for 27s"); the rest is kept on the message so it can be logged in debug builds.
 */
export interface AiChatUsageModel {
  model?: string;
  input_tokens?: number;
  cached_input_tokens?: number;
  output_tokens?: number;
  total_tokens?: number;
  total_cost?: number;
  currency?: string;
  /** Seconds from the question to the finished answer. */
  elapsed_seconds?: number;
}

/**
 * A thumbs rating on an assistant reply. `null` is "not rated" - re-clicking the lit thumb
 * clears it back to that, which is the behaviour customers expect from every other chat.
 */
export type AiChatFeedbackRating = 'up' | 'down';

/**
 * The bucket a thumbs-down is filed under. Same ids as the console's own picker, so both
 * surfaces land in one set of buckets in the review section rather than two.
 */
export type AiChatFeedbackCategory =
  | 'incorrect_or_incomplete'
  | 'not_what_i_asked_for'
  | 'slow_or_buggy'
  | 'style_or_tone'
  | 'safety_or_legal_concern'
  | 'other';

/** The picker's options, in the order they are shown. Mirrors the console's list exactly. */
export const AI_CHAT_FEEDBACK_CATEGORIES: ReadonlyArray<{ id: AiChatFeedbackCategory; name: string }> = [
  { id: 'incorrect_or_incomplete', name: 'Incorrect or incomplete' },
  { id: 'not_what_i_asked_for', name: 'Not what I asked for' },
  { id: 'slow_or_buggy', name: 'Slow or buggy' },
  { id: 'style_or_tone', name: 'Style or tone' },
  { id: 'safety_or_legal_concern', name: 'Safety or legal concern' },
  { id: 'other', name: 'Other' }
];

/**
 * An image the customer attached to a turn, once the service has accepted it.
 *
 * `url` is a presigned view URL and expires; it is good for the life of the open page but is
 * deliberately NOT persisted with the transcript, since a stored one would be dead on the next
 * visit. `GET attachments/{uuid}/url` mints a fresh one when an old turn needs re-displaying.
 */
export interface AiChatAttachmentModel {
  attachment_uuid: string;
  file_name: string;
  content_type: string;
  size_bytes: number;
  width?: number | null;
  height?: number | null;
  url?: string | null;
}

/**
 * An image picked in the composer but not yet sent, with its upload state.
 *
 * Lives only as long as the composer holds it: once the turn is sent these become
 * AiChatAttachmentModel on the user message, and the pending list is emptied.
 */
export interface AiChatPendingAttachmentModel {
  /** Local id, so the strip can track rows before the service has minted a uuid. */
  id: string;
  file: File;
  /** Object URL for the local thumbnail. Must be revoked when the row goes away. */
  preview_url: string;
  /** Minted by `attachments/uploads`; absent until authorisation returns. */
  attachment_uuid?: string;
  status: 'uploading' | 'ready' | 'error';
  /** Why it failed, shown under the strip. */
  error?: string;
}

/**
 * A single message inside an AI support conversation.
 */
export class AiChatMessageModel {
  id!: string;
  role!: AiChatMessageRole;
  message!: string;
  /**
   * The service's id for this stored message - what a thumbs rating is filed against.
   *
   * On a reply streamed in this browser it comes from the stream's `complete` frame; on one read
   * back from history it is the stored message's `uuid`. Never the client-generated uuid the
   * turn was sent under: that names the question, and the service 404s a rating made with it.
   * Absent when the turn was stopped or refused before the service sent an id.
   */
  message_uuid?: string;
  /** ISO timestamp. Generated client-side: this channel returns no message ids or timestamps. */
  created_at!: string;
  /** True from the moment the assistant placeholder is created until the stream closes. */
  is_streaming?: boolean;
  /** Set when the turn failed, so the bubble can render an error state instead of an empty reply. */
  has_error?: boolean;
  /** Tool calls seen this turn, in arrival order. */
  tool_activity?: AiChatToolActivityModel[];
  /**
   * What to show while the reply is still empty - the running tool's label, or a default.
   * Cleared the moment the first `answer` text arrives.
   */
  status_label?: string;
  /** Usage frame for the turn, if one arrived. */
  usage?: AiChatUsageModel;
  /**
   * The customer's thumbs rating, or null/undefined when they have not rated this reply.
   *
   * Held on the message and persisted with the transcript, and read back from the service
   * whenever the transcript is (opening from history, or the re-read after a reload). See
   * AiChatConversationStore.vote().
   */
  feedback?: AiChatFeedbackRating | null;
  /** Images sent with this turn. User messages only. */
  attachments?: AiChatAttachmentModel[];
  /**
   * SKUs to show as product cards beside the entries that link to them. Assistant messages only.
   *
   * Every `product_cards` payload on the turn flattened in order - a second frame appends, it
   * never replaces. Persisted with the transcript, since it is only SKUs and the cards are
   * re-resolved from the product API on display. Never shown to the customer.
   */
  product_skus?: string[];
  /**
   * Each SKU's product page, index for index with `product_skus` (the payload's
   * `products[].product_link`) - an entry takes the card whose link equals its own. Null where
   * the payload gave none, which is every card on a reply stored before the field existed.
   */
  product_links?: (string | null)[];
  /**
   * Each SKU's `products[].recommended` flag, index for index with `product_skus` - the entry
   * linking to the flagged product carries the "Recommended" badge. At most one is true; the
   * reply text never marks it.
   */
  product_recommended?: boolean[];
}

/**
 * A conversation. The service allocates nothing - `conversation_uuid` is generated client-side
 * when the widget opens and reused for every turn so the agent's memory carries across; a new
 * value starts over with no history.
 */
export class AiChatThreadModel {
  /** The client-generated v4 `conversation_uuid` sent with every turn. */
  id!: string;
  messages: AiChatMessageModel[] = [];
}

/**
 * One row in the history rail: a conversation the service has stored for this customer.
 *
 * These are not created by the client. `public/chat` persists every turn against the caller's
 * identity, deriving the title from the first question, so a conversation appears in this list
 * as soon as its first turn has been sent - which is why the rail shows nothing for a thread
 * that was started but never used.
 */
export interface AiChatConversationModel {
  /** The `conversation_uuid`, and the id every other call is keyed by. */
  id: string;
  /** Server-derived from the opening question; renameable. */
  title: string;
  /** Stored turns, both roles. Absent on conversations stored before it was recorded. */
  message_count: number;
  created_at: string;
  updated_at: string;
}

/**
 * One page of history.
 *
 * `next_cursor` is opaque - it encodes the last row's sort position, so it must be passed back
 * untouched and never built by hand. Null means the end of the list, which is the only reliable
 * end-of-list signal: a full page can still be the last one.
 */
export interface AiChatConversationPageModel {
  items: AiChatConversationModel[];
  next_cursor: string | null;
}

/**
 * One page of a stored transcript. Unlike the conversation cursor this one is a sequence
 * number, so it is a number and not an opaque string.
 */
export interface AiChatMessagePageModel {
  items: AiChatMessageModel[];
  next_cursor: number | null;
}

/**
 * A refusal from the chat endpoint. Refusals (401/403/422/429) arrive as a plain JSON envelope
 * *before* the stream opens, never as a frame inside it, so they are always thrown from the
 * response-status check rather than mid-parse.
 */
export class AiChatApiError extends Error {
  /** HTTP status; 0 when the request never completed (offline, DNS, CORS). */
  status: number;
  /** `message` from the envelope - safe to surface, except for the 401s (see AiChatService). */
  apiMessage: string;
  /** 429 only: the server's UTC reset instant, rendered in the customer's own timezone. */
  retryAt: string | null;
  /** 429 only: the same information as a countdown. */
  retryAfterSeconds: number | null;
  /** Present on 403/422/429 - quote it when reporting a problem to the API team. */
  requestUuid: string | null;

  constructor(init: {
    status: number;
    apiMessage: string;
    retryAt?: string | null;
    retryAfterSeconds?: number | null;
    requestUuid?: string | null;
  }) {
    super(init.apiMessage);
    this.name = 'AiChatApiError';
    this.status = init.status;
    this.apiMessage = init.apiMessage;
    this.retryAt = init.retryAt ?? null;
    this.retryAfterSeconds = init.retryAfterSeconds ?? null;
    this.requestUuid = init.requestUuid ?? null;
  }
}
